import {
  generateHTML,
  generateJSON,
  mergeAttributes,
  Node,
} from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";

/**
 * Mustache <-> visual editor plumbing for message templates.
 *
 * The backend stores Mustache: `{{name}}` (HTML-escaped value), `{{{name}}}` (raw value, used
 * for links and HTML fragments) and `{{#x}}...{{/x}}` sections. In the visual editor every tag
 * found in text becomes an atomic "chip" node, so it cannot be half-deleted or split by
 * formatting. Tags inside attributes (`<a href="{{{resetUrl}}}">`) are left in the attribute,
 * where the editor keeps them verbatim.
 *
 * Email layout HTML (tables, inline styles, <head>, Outlook conditional comments) cannot survive
 * a rich-text editor's schema. `visualEditingBlocker` round-trips the body through the exact
 * editor schema and only allows visual editing when nothing would be lost; otherwise the
 * template is edited as source, with the rendered preview alongside.
 */

export const MUSTACHE_TAG = /\{\{\{?[\s\S]*?\}?\}\}/g;

export const MustacheVariable = Node.create({
  name: "mustacheVariable",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,
  draggable: false,

  addOptions() {
    return { labelFor: (token) => token };
  },

  addAttributes() {
    return {
      token: {
        default: "",
        parseHTML: (element) => element.getAttribute("data-mustache") || "",
        renderHTML: (attributes) => ({ "data-mustache": attributes.token }),
      },
    };
  },

  parseHTML() {
    return [{ tag: "span[data-mustache]" }];
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      "span",
      mergeAttributes(HTMLAttributes, {
        class: "mt-chip",
        contenteditable: "false",
        role: "img",
        "aria-label": `Variable ${this.options.labelFor(node.attrs.token)}`,
      }),
      this.options.labelFor(node.attrs.token),
    ];
  },

  renderText({ node }) {
    return node.attrs.token;
  },
});

/** Extensions shared by the live editor and the round-trip check; they must stay identical. */
export const buildEditorExtensions = (labelFor) => [
  StarterKit.configure({ codeBlock: false, code: false }),
  Link.configure({
    openOnClick: false,
    autolink: false,
    linkOnPaste: false,
    HTMLAttributes: { target: null, rel: null, class: null },
  }),
  Image.configure({ inline: false, allowBase64: false }),
  MustacheVariable.configure(labelFor ? { labelFor } : {}),
];

const parseBody = (html) =>
  new DOMParser().parseFromString(`<body>${html || ""}</body>`, "text/html")
    .body;

/** `{{name}}` in text nodes -> `<span data-mustache="{{name}}"></span>`. Attributes untouched. */
export const tokensToChips = (html) => {
  const body = parseBody(html);
  const doc = body.ownerDocument;
  const walker = doc.createTreeWalker(body, NodeFilter.SHOW_TEXT);
  const textNodes = [];
  while (walker.nextNode()) textNodes.push(walker.currentNode);

  textNodes.forEach((textNode) => {
    const text = textNode.nodeValue;
    MUSTACHE_TAG.lastIndex = 0;
    if (!MUSTACHE_TAG.test(text)) return;
    MUSTACHE_TAG.lastIndex = 0;

    const fragment = doc.createDocumentFragment();
    let last = 0;
    let match;
    while ((match = MUSTACHE_TAG.exec(text)) !== null) {
      if (match.index > last) {
        fragment.appendChild(doc.createTextNode(text.slice(last, match.index)));
      }
      const span = doc.createElement("span");
      span.setAttribute("data-mustache", match[0]);
      fragment.appendChild(span);
      last = match.index + match[0].length;
    }
    if (last < text.length)
      fragment.appendChild(doc.createTextNode(text.slice(last)));
    textNode.parentNode.replaceChild(fragment, textNode);
  });
  return body.innerHTML;
};

/** Inverse of tokensToChips: every chip span becomes its literal Mustache tag again. */
export const chipsToTokens = (html) => {
  const body = parseBody(html);
  body.querySelectorAll("span[data-mustache]").forEach((span) => {
    span.replaceWith(
      body.ownerDocument.createTextNode(span.getAttribute("data-mustache")),
    );
  });
  return body.innerHTML;
};

const BLOCK_TAGS = "p|div|li|ul|ol|h[1-6]|br|img|blockquote|hr|pre";
const AROUND_BLOCK = new RegExp(
  `\\s*(</?(?:${BLOCK_TAGS})\\b[^>]*>)\\s*`,
  "gi",
);

/** Canonical form for comparing two bodies; ignores insignificant whitespace only. */
export const normalizeHtml = (html) =>
  parseBody(html)
    .innerHTML.replace(/\s+/g, " ")
    .replace(AROUND_BLOCK, "$1")
    .replace(/<p><\/p>/g, "")
    .trim();

const LAYOUT_MARKUP =
  /<!doctype|<html[\s>]|<head[\s>]|<body[\s>]|<table[\s>]|<style[\s>]|<!--|\sstyle\s*=|<center[\s>]|<font[\s>]/i;

/** What the visual editor would output for `html`, converted back to Mustache tags. */
export const roundTrip = (html, extensions = buildEditorExtensions()) =>
  chipsToTokens(
    generateHTML(generateJSON(tokensToChips(html), extensions), extensions),
  );

/**
 * Null when the body can be edited visually without losing anything; otherwise a sentence
 * explaining why it must be edited as source.
 */
export const visualEditingBlocker = (html) => {
  if (!html || !html.trim()) return null;
  if (LAYOUT_MARKUP.test(html)) {
    return "This template uses email layout HTML (tables, inline styles or a document head) that the visual editor would strip. Edit it as HTML source; the preview shows the result.";
  }
  try {
    if (normalizeHtml(roundTrip(html)) !== normalizeHtml(html)) {
      return "The visual editor would change this template's markup. Edit it as HTML source; the preview shows the result.";
    }
  } catch (e) {
    return "This template could not be loaded into the visual editor. Edit it as HTML source.";
  }
  return null;
};

/** Human label for a chip, e.g. `{{{resetUrl}}}` -> `Reset password link`. */
export const labelForToken = (token, variables = []) => {
  const name = String(token || "")
    .replace(/^\{\{\{?\s*[#^/&]?\s*/, "")
    .replace(/\s*\}?\}\}$/, "");
  const prefix = /^\{\{\s*#/.test(token)
    ? "if "
    : /^\{\{\s*\^/.test(token)
    ? "unless "
    : /^\{\{\s*\//.test(token)
    ? "end "
    : "";
  const match = variables.find((v) => v.name === name);
  return `${prefix}${match ? match.label : name}`;
};

/** Inserts `text` into a textarea/input at its caret and returns the new value and caret. */
export const insertAtCaret = (element, value, text) => {
  const current = value || "";
  const start = element?.selectionStart ?? current.length;
  const end = element?.selectionEnd ?? current.length;
  return {
    value: current.slice(0, start) + text + current.slice(end),
    caret: start + text.length,
  };
};
